import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Divider,
  Fab,
  FormControlLabel,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RefreshIcon from "@mui/icons-material/Refresh";
import { colors } from "brand";
import { TableSkeleton } from "common-fe";
import { useTranslation } from "i18n-fe";
import { RIDE_STATUSES } from "score-common";
import { useAnnouncerContext } from "../context/AnnouncerContext";
import { ringsFor } from "../utils/rings";
import { useClassesForShow } from "../hooks/useClassesForShow";
import { useRidesForRing } from "../hooks/useRidesForRing";
import { useBreaksForRing } from "../hooks/useBreaksForRing";
import { usePeople, useHorses, useTestsForRides } from "../hooks/useRoster";
import { useRideStatuses } from "../hooks/useRideStatuses";
import { useRideSheets } from "../hooks/useRideSheets";
import { useRingStateForRing } from "../hooks/useRingStateForRing";
import {
  buildOrderOfGoRows,
  findNextEvent,
  findRoleRideId,
  groupRowsByDay,
  ROW_KIND,
  RIDE_ROLE,
  resolveRingTruth,
  resolveLiveClassId,
} from "../utils/orderOfGo";
import { formatTime, formatVenueClock, venueDayKey, venueTodayKey, dayKeyLabel } from "../utils/formatTime";
import { formatClockTime } from "../utils/formatClock";
import RideRow from "./RideRow";
import ClassResults from "./ClassResults";

// #642 — THE app: one live, time-ordered running order for ONE ring.
//
// Read-only by construction. The announcer never writes a score, never changes a
// status, never touches FVD — which is what keeps this screen small.
//
// Freshness, per docs/score/announcer.md:
//   - ride STATUSES change constantly during a class -> poll every 5s
//     (useRideStatuses; it already pauses on a hidden tab and refreshes on
//     return, so nothing here needs a visibility listener of its own).
//   - rides / breaks / classes change on a scratch, a schedule reorder, or a
//     re-ingest -> refetched on the SAME 5s beat (#692 — a class
//     reorder/scratch/moved break must show up on the very next poll, and an
//     FVD change must be visible within 5s). #2190 — no local cache anymore;
//     a failed refetch just leaves whatever's already on screen alone.
//     people/horses stay on their own one-shot fetch (useRoster.js) —
//     org-wide roster data that isn't part of the running order.
//
// #692 polling cost, one ring/terminal, one show day (all single-query GSI
// reads, no per-class fan-out — #637's showId/ring filter on GET /ride):
//   GET /ride?showId&ring      (ridesByShow GSI)    -> 720/hr
//   GET /break?showId&ring     (breaksByShow GSI)   -> 720/hr
//   GET /class?showId                               -> 720/hr
//   POST /rides/status (batch)                       -> 720/hr
//   ~2,880 reads/hr/terminal. Cheap: each is one indexed query, not a scan,
//   and a full 8-hour show day is well under 25k reads per terminal even with
//   several terminals running at once.
const STATUS_POLL_MS = 5000;
const ENTITY_POLL_MS = 5000;
// #645 — how long the screen can go without a successful status refresh
// before it stops calling itself "live", even while `online` is still true.
// A single missed poll (a blip) shouldn't flash the banner; four missed polls
// (STATUS_POLL_MS * 4) means something's actually wrong upstream.
const STALE_THRESHOLD_MS = STATUS_POLL_MS * 4;
// How often the banner's own clock re-checks staleness / re-renders its
// "HH:MM" text. Independent of the entity/status polls — this never touches
// the network, it just re-evaluates wall-clock math.
const CLOCK_TICK_MS = 10000;
// #691 — the sticky header's live clock + countdown want a proper ticking
// feel, so they get their own faster tick, separate from the freshness
// banner's coarser one above. Still fetches nothing — pure wall-clock math.
const LIVE_TICK_MS = 1000;

// #926 — Auto Scroll (#691) and the all-days toggle (#892) used to be single
// booleans shared across the whole session. Now that a pill row lets the
// announcer free-switch rings (no PIN), each ring needs to remember its OWN
// preferences — Ring A can be today+auto-scroll while Ring B is all-days+
// no-scroll. Both settings live in one map, keyed by ring id, under a single
// storage key (JSON, not the old two flat booleans — those legacy keys are
// simply abandoned; nothing reads them anymore).
const RING_SETTINGS_STORAGE_KEY = "announcer-fe.ringSettings";

// Defaults for a ring that has never been switched to before — matches the
// pre-#926 defaults (auto-scroll ON, today-only) so a first-time ring doesn't
// surprise anyone.
const DEFAULT_RING_SETTINGS = { autoScrollEnabled: true, showAllDays: false };

function loadRingSettingsMap() {
  try {
    const raw = localStorage.getItem(RING_SETTINGS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function persistRingSettingsMap(map) {
  try {
    localStorage.setItem(RING_SETTINGS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage may be unavailable (private browsing, etc.) — non-fatal.
  }
}

function settingsForRing(map, ring) {
  const stored = ring ? map[ring] : null;
  return { ...DEFAULT_RING_SETTINGS, ...(stored || {}) };
}

// "2h 5m" / "5m" — coarse on purpose; this is "how long until the next
// event", not a race-clock stopwatch.
function formatCountdown(ms) {
  if (ms <= 0) return "0m";
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

// The next event's own label — a break announces its label (or the generic
// "Break"), a ride announces its class number + rider, matching the fields
// already shown on its row.
function nextEventLabel(row, t) {
  if (!row) return "";
  if (row.kind === ROW_KIND.BREAK) return row.label || t("oog.breakLabel");
  return [row.classNumber, row.riderName].filter(Boolean).join(" — ");
}

// The entity-list revalidate beat (#692 — 5s, same cadence as the status poll).
// `online` is frozen true (#912), so this always ticks now; it still pauses
// while the tab is hidden — an announcer who has switched away is not reading
// the screen. Downstream fetches are caught/cached, so a real offline stretch
// degrades gracefully rather than needing this tick paused.
function useEntityTick(online) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!online) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      setTick((n) => n + 1);
    }, ENTITY_POLL_MS);
    return () => clearInterval(id);
  }, [online]);
  return tick;
}

// #645 — a plain wall clock, always ticking (unlike useEntityTick, which
// pauses offline — staleness must be detectable EXACTLY when we're offline).
// Purely a re-render trigger for the freshness banner's math; fetches nothing.
function useNow(intervalMs) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function ClassHeader({ row }) {
  const { t } = useTranslation("announcer");
  const heading = [row.classNumber, row.className].filter(Boolean).join(" — ");
  return (
    <Box data-testid="class-header" data-class-id={row.classId} sx={{ pt: 3, pb: 0.5 }}>
      <Typography
        variant="overline"
        sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 1 }}
      >
        {t("oog.classLabel")}
      </Typography>
      <Typography variant="h4" fontWeight={800} sx={{ color: "text.primary" }}>
        {heading || t("oog.classLabel")}
      </Typography>
      <Divider sx={{ mt: 1, borderBottomWidth: 2 }} />
    </Box>
  );
}

// #892 — the all-rides/today-only toggle's day grouping. One level ABOVE a
// ClassHeader (a day can hold several classes), so it's bigger and carries
// its own foxOrange rule rather than reusing ClassHeader's divider — the
// announcer needs to tell "new class, same day" apart from "new day" at a
// glance. In the today-only view groupRowsByDay() still emits exactly one of
// these, at the very top: that's the "show the single day prominently"
// half of the AC, using the SAME component rather than a second bespoke one.
function DayHeader({ row }) {
  return (
    <Box data-testid="day-header" data-day-key={row.dayKey || ""} sx={{ pt: 2, pb: 1 }}>
      <Typography
        variant="h3"
        fontWeight={900}
        sx={{ color: "text.primary", letterSpacing: 1 }}
      >
        {dayKeyLabel(row.dayKey)}
      </Typography>
      <Divider sx={{ mt: 1, borderBottomWidth: 4, borderColor: colors.foxOrange }} />
    </Box>
  );
}

// A break is not a ride and never can be (#585/#638) — it gets its own row, and
// it is one of the things the announcer actually announces.
function BreakRow({ row }) {
  const { t } = useTranslation("announcer");
  return (
    <Box
      data-testid="break-row"
      data-break-id={row.id}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 2,
        borderRadius: 3,
        border: "2px dashed",
        borderColor: colors.foxOrange,
        // Deliberately no fill: a tint that reads in light mode goes muddy in
        // dark, and the dashed orange border carries the signal in both.
      }}
    >
      <Typography variant="h5" fontWeight={700} sx={{ minWidth: 96 }}>
        {formatTime(row.scheduledTime)}
      </Typography>
      <RestaurantIcon sx={{ color: colors.foxOrange }} />
      <Typography variant="h5" fontWeight={800} sx={{ color: "text.primary" }}>
        {row.label || t("oog.breakLabel")}
      </Typography>
    </Box>
  );
}

export default function OrderOfGo() {
  const { t } = useTranslation("announcer");
  const { selectedShow, selectedRing, switchRing, online, browserOnline, ringResolved } =
    useAnnouncerContext();
  const showId = selectedShow?.id;

  // #926 — per-ring settings map (auto-scroll, all-days), keyed by ring id.
  // `currentRingSettings` is whichever ring is active RIGHT NOW; switching
  // rings via the pill row recomputes it automatically (no extra effect
  // needed) since it's derived straight from `selectedRing`.
  const [ringSettingsMap, setRingSettingsMap] = useState(loadRingSettingsMap);
  const currentRingSettings = useMemo(
    () => settingsForRing(ringSettingsMap, selectedRing),
    [ringSettingsMap, selectedRing],
  );
  // #1857 — `selectedRing` starts `null` and resolves asynchronously
  // (AnnouncerContext's own, separate GET /class fetch -> resolveDefaultRing,
  // #1740). Before it resolves, `settingsForRing`'s `ring ? map[ring] : null`
  // makes ANY write here a no-op-in-spirit: it would key onto the string
  // "null" (computed property coercion) and get silently abandoned the
  // instant the real ring id lands, reading back as DEFAULT_RING_SETTINGS
  // regardless of what the operator just chose. Guarding the write (and
  // disabling the switches below while unresolved, so there's nothing TO
  // write yet) closes that window instead of leaving a setting that can be
  // set and then silently lost.
  const updateRingSetting = useCallback(
    (key, value) => {
      if (!selectedRing) return;
      setRingSettingsMap((prev) => {
        const next = {
          ...prev,
          [selectedRing]: { ...settingsForRing(prev, selectedRing), [key]: value },
        };
        persistRingSettingsMap(next);
        return next;
      });
    },
    [selectedRing],
  );

  // Entity lists move only on a scratch or a re-ingest, so a slow beat is plenty
  // (statuses have their own, much faster, poll below). Bumping the tick is what
  // makes the entity hooks below revalidate — they re-run on its deps.
  const entityTick = useEntityTick(online);

  // #892 — all-rides vs today-only toggle. Per-ring (#926): default OFF
  // (today-only) for a never-seen ring — the announcer normally works one day
  // at a time, matching scribe-fe's #910 "today by default" intent.
  const showAllDays = currentRingSettings.showAllDays;
  const setShowAllDays = useCallback(
    (enabled) => updateRingSetting("showAllDays", enabled),
    [updateRingSetting],
  );
  // "Today" as the VENUE sees it (#688), never the browser's own zone. #1813 —
  // venueTodayKey takes a genuine instant, so (unlike venueDayKey(scheduledTime)
  // below, which stays on the fixed FVD stamp zone) it needs the show's real
  // venueTimezone to land on the same calendar day; see formatTime.js's
  // venueTodayKey comment for the full reasoning. Cheap to recompute — this
  // component already re-renders on every poll/clock tick.
  const todayKey = venueTodayKey(undefined, selectedShow?.venueTimezone);

  // #692 — classes now revalidate on the same entityTick as rides/breaks
  // (previously it never accepted a nonce at all, so a renamed/renumbered or
  // newly-added class could sit stale in the header forever after setup).
  const {
    data: classes,
    loading: classesLoading,
    refetch: refetchClasses,
    refreshing: classesRefreshing,
  } = useClassesForShow(showId, entityTick);

  // #926 — every ring for THIS show, from the already-fetched class list
  // (same source AnnouncerContext's default-ring resolution uses —
  // score-api has no ring-listing route). Feeds the pill row below;
  // switching a pill just calls AnnouncerContext's ungated `switchRing`, no
  // PIN involved.
  const rings = useMemo(() => ringsFor(classes), [classes]);
  const handleRingSwitch = useCallback(
    (ring) => {
      if (ring && ring !== selectedRing) switchRing(ring);
    },
    [selectedRing, switchRing],
  );

  const {
    data: rides,
    loading: ridesLoading,
    refetch: refetchRides,
    refreshing: ridesRefreshing,
  } = useRidesForRing(showId, selectedRing, entityTick);
  const {
    data: breaks,
    refetch: refetchBreaks,
    refreshing: breaksRefreshing,
  } = useBreaksForRing(showId, selectedRing, entityTick);
  const { data: people } = usePeople();
  const { data: horses } = useHorses();

  // #892 — today-only view filters the RAW entities BEFORE buildOrderOfGoRows
  // ever sees them (rather than filtering the built rows afterwards), so
  // CURRENT/ON_DECK/NEXT role assignment — and the test-fetch/status-poll ID
  // lists right below — stay scoped to today's rides, exactly like the
  // announcer's existing single-day behavior always has. A ride/break with no
  // derivable day (no scheduledTime at all) is kept regardless of the toggle:
  // it can't be dated, so it shouldn't silently vanish because of that.
  const filteredRides = useMemo(() => {
    if (showAllDays) return rides || [];
    return (rides || []).filter((r) => {
      const day = venueDayKey(r.scheduledTime);
      return !day || day === todayKey;
    });
  }, [rides, showAllDays, todayKey]);
  const filteredBreaks = useMemo(() => {
    if (showAllDays) return breaks || [];
    return (breaks || []).filter((b) => {
      const day = venueDayKey(b.scheduledTime);
      return !day || day === todayKey;
    });
  }, [breaks, showAllDays, todayKey]);

  const testIds = useMemo(
    () => (filteredRides || []).map((r) => r.testId).filter(Boolean),
    [filteredRides],
  );
  const { data: tests } = useTestsForRides(testIds);

  // Poll the SINGLE server-derived status for every ride in the ring (#544/#546).
  // Only meaningful while online — a hidden tab is already handled inside the hook.
  const rideIds = useMemo(() => (filteredRides || []).map((r) => r.id), [filteredRides]);
  const {
    statuses,
    lastUpdated: statusesLastUpdated,
    loading: statusesRefreshing,
    refresh: refreshStatuses,
  } = useRideStatuses(rideIds, {
    intervalMs: STATUS_POLL_MS,
    enabled: online && rideIds.length > 0,
  });
  // #2070 — ScoreSheets for the same ride-id set, so deriveRideTermReason
  // can resolve a sheet-level elimination that hasn't been denormalized
  // onto ride.terminationReason yet (see useRideSheets.js).
  const { sheetsByRideId, refresh: refreshSheets } = useRideSheets(rideIds, {
    enabled: online && rideIds.length > 0,
  });

  // #2131 (docs/score/ring-truth.md) — the ring's OWN stored ring-state row,
  // the ONE input CURRENT/ON_DECK role assignment reads (assignRoles in
  // orderOfGo.js). Same entityTick revalidate beat as classes/rides/breaks
  // (#692) rather than a separate poll cadence for one small row.
  const {
    data: ringState,
    loading: ringStateLoading,
    refetch: refetchRingState,
  } = useRingStateForRing(showId, selectedRing, entityTick);
  // Rule 8 (docs/score/ring-truth.md) — resolved once here so the sticky
  // "no class in progress" banner below and buildOrderOfGoRows's role
  // assignment read the identical answer, never two separate opinions.
  const ringTruth = useMemo(() => resolveRingTruth(ringState), [ringState]);
  // #2197 — true only once ring-state has answered for a REAL ring, not
  // useRingStateForRing's synthetic "ring not resolved yet" settle
  // (undefined, see that hook's comment). `ringStateLoading` alone can't
  // tell these apart: useRemoteResource's `loading` only ever covers the
  // FIRST fetch this mount has ever settled, and the ring-not-known case
  // settles first (near-instantly, before the ring even resolves), so
  // `ringStateLoading` reads false throughout the real, ring-resolved fetch
  // too. `ringState !== undefined` is what actually distinguishes "no
  // answer yet" from "server confirmed no live class" (a real null).
  const ringStateKnown = !ringStateLoading && ringState !== undefined;
  // #2087 — the batch status poll's OWN "we don't know yet" signal, parallel
  // to #2086's `resultsUnknown` in ClassResults. `statusesLastUpdated` is set
  // only after a fetch actually lands (packages/common-fe/src/utils/useRideStatuses.js);
  // it never resets once set (a later failure keeps the last-known statuses
  // per #705's degraded mode, "known, stale" rather than "unknown"), so this
  // is true exactly while the poll has never once answered OR is failing
  // persistently before its first success — precisely the window where
  // normalizeStatus's PENDING default (orderOfGo.js) is a guess, not a fact.
  const statusesUnknown = statusesLastUpdated == null;

  // #908 — manual "refresh now" for a ring announcer who wants the latest
  // running order without waiting for the next poll, and without the
  // reload-the-whole-kiosk workaround (this is a PIN-locked kiosk, #734 — no
  // reload affordance exists). Fires all four data sources that make up the
  // Order of Go: classes/rides/breaks each go through useRemoteResource's
  // refetch() (forces a fresh fetch immediately), and refreshStatuses()
  // forces the status poll's fetch immediately instead of waiting for
  // STATUS_POLL_MS.
  // Deliberately does NOT touch scroll position — scrollToCurrent (#909) is
  // the only path that scrolls, and it stays gated on autoScrollEnabled
  // regardless of what triggered the data change underneath it.
  const handleRefresh = useCallback(() => {
    refetchClasses();
    refetchRides();
    refetchBreaks();
    refreshStatuses();
    refreshSheets();
    refetchRingState();
  }, [
    refetchClasses,
    refetchRides,
    refetchBreaks,
    refreshStatuses,
    refreshSheets,
    refetchRingState,
  ]);
  const refreshing =
    classesRefreshing || ridesRefreshing || breaksRefreshing || statusesRefreshing;

  // #645 — freshness banner. The status poll's own lastUpdated is the only
  // source now (#2190 removed the setup-time prefetch timestamp fallback
  // along with the rest of local caching); a session that has never had a
  // successful status poll simply has no "last updated" time yet.
  const lastUpdated = statusesLastUpdated ?? null;
  const now = useNow(CLOCK_TICK_MS);
  const isStale = Boolean(lastUpdated) && now - lastUpdated.getTime() > STALE_THRESHOLD_MS;
  // #691 — sticky header's live clock + countdown. Separate, faster tick from
  // the freshness banner's `now` above; pure re-render trigger, no network.
  const liveNow = useNow(LIVE_TICK_MS);
  // "Not live" whenever we're offline OR the data we're showing has gone
  // stale despite being nominally online (a dead upstream can still report
  // navigator.onLine === true) — the screen must never claim to be live when
  // it isn't sure.
  // #912 — `online` no longer tracks navigator.onLine (it's forced true so
  // reads never get gated off by a flaky-wifi false-negative). Drive the "not
  // live" banner off the RAW browser flag + real data staleness instead, so it
  // still tells the announcer honestly when the screen isn't live.
  const showFreshnessBanner = !browserOnline || isStale;

  const rows = useMemo(
    () =>
      buildOrderOfGoRows({
        rides: filteredRides,
        breaks: filteredBreaks,
        classes: classes || [],
        people: people || [],
        horses: horses || [],
        tests: tests || [],
        statuses: statuses || {},
        sheetsByRideId,
        ringState,
      }),
    [
      filteredRides,
      filteredBreaks,
      classes,
      people,
      horses,
      tests,
      statuses,
      sheetsByRideId,
      ringState,
    ],
  );

  // #892 — the all-rides/today-only toggle's own display layer, on top of
  // `rows`: inserts venue-local day headings without touching role
  // assignment (CURRENT/ON_DECK/NEXT already reflect the filtered entities
  // above) or which row anything else below refers to — findNextEvent,
  // findRoleRideId, rideRowsByClassId etc. all still read the plain `rows`.
  const displayRows = useMemo(() => groupRowsByDay(rows, todayKey), [rows, todayKey]);

  // #691 — sticky header's countdown: next ride OR break, whichever is
  // sooner (breaks are real `score-break` rows and already inline in `rows`).
  const nextEvent = useMemo(() => findNextEvent(rows, liveNow), [rows, liveNow]);

  // #644/#2131 — results/placings. The "live" class is the ring-state row's
  // OWN classId whenever it's in progress: that's the one whose results can
  // still change from one status poll to the next, so it's the only one
  // that needs a slow results poll of its own (docs/score/announcer.md
  // freshness table). Every other class fetches once.
  //
  // Deliberately NOT "whichever row has role CURRENT" (judge finding (d)):
  // ringTruth.rideId is nullable (rule 5 / AC17 — Start class names a class
  // before any of its rides has been opened), so a genuinely in-progress
  // class can have zero rows tagged CURRENT. resolveLiveClassId reads
  // ringTruth directly and has no such gap.
  const liveClassId = resolveLiveClassId(ringTruth);

  const rideRowsByClassId = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      if (row.kind !== ROW_KIND.RIDE || !row.classId) continue;
      if (!map.has(row.classId)) map.set(row.classId, []);
      map.get(row.classId).push(row);
    }
    return map;
  }, [rows]);

  // classId -> raw GET /class/:classId/results array, lifted up from each
  // ClassResults instance so RideRow can badge its own placing too (the
  // class-level list and the per-ride badge are fed by the SAME fetch/poll,
  // never a second independent one).
  const [resultsByClassId, setResultsByClassId] = useState({});
  const handleClassResults = useCallback((classId, classResults) => {
    setResultsByClassId((prev) => {
      if (prev[classId] === classResults) return prev;
      return { ...prev, [classId]: classResults };
    });
  }, []);

  // ---- auto-scroll to the current ride, without ever fighting the announcer --
  const currentRideId = findRoleRideId(rows, RIDE_ROLE.CURRENT);
  const currentRef = useRef(null);
  // #2112 — the sticky header (below) is `position: "fixed"` under the normal
  // app shell, not `sticky`: `common-fe`'s MainContent wraps this component in
  // a Box that sets `overflow: "auto"` but only ever constrains `minHeight`,
  // so it grows to fit its content and never scrolls itself — the WINDOW ends
  // up scrolling instead, while MainContent still counts as the nearest CSS
  // "scroll container" ancestor for any `position: sticky` descendant, whose
  // scrollTop then never moves, so a sticky header would never actually
  // stick. `position: fixed` sidesteps that ancestor entirely (fixed pins to
  // the viewport regardless of any ancestor's overflow, as long as nothing
  // between here and the root sets a transform/filter/contain — none of
  // AppLayout/MainContent/this component's own tree do). headerRef measures
  // the header's own rendered height (it wraps at narrow widths, #1875) so
  // the spacer Box right after it can reserve the same space the header used
  // to occupy in normal flow, keeping everything below unaffected.
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setHeaderHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const [currentOffScreen, setCurrentOffScreen] = useState(false);
  // #691 — the "Auto Scroll to Current Ride" switch. OFF leaves scrolling
  // entirely to the announcer (the effect below simply doesn't fire); ON
  // restores today's existing behavior. Per-ring (#926) — same map as
  // showAllDays above, so it survives a reload AND a ring switch restores
  // THIS ring's own preference rather than whatever the last ring had.
  const autoScrollEnabled = currentRingSettings.autoScrollEnabled;
  const setAutoScrollEnabled = useCallback(
    (enabled) => updateRingSetting("autoScrollEnabled", enabled),
    [updateRingSetting],
  );

  // #909 — the ONE place that ever calls scrollIntoView for "jump to
  // current". Gated here so every caller (the effect below AND the "back to
  // current" FAB, which docs/score/announcer.md describes as part of the
  // same auto-scroll feature — "get back with one tap") is a no-op while the
  // switch is OFF. OFF must mean zero programmatic scrolling, full stop.
  const scrollToCurrent = useCallback(() => {
    if (!autoScrollEnabled) return;
    currentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [autoScrollEnabled]);

  // #919 — a LIVE-SHOW regression. Confirming a ride's marks (all judges
  // confirmed, #697/#698) flips that ride's OWN status off IN_PROGRESS.
  // #2131 changed WHERE CURRENT comes from (the ring's stored ring-state
  // row, via assignRoles() in orderOfGo.js, not a per-render hunt for an
  // IN_PROGRESS ride) but not the heuristic below: a scribe moving on to a
  // different ride in the ring still reassigns CURRENT, and the auto-scroll
  // yanked the screen away from the ride the announcer was mid-read on,
  // right as its judge marks were revealed — exactly when the operator
  // needs to control position, not have it taken from them.
  //
  // Distinguish WHY current changed by looking at the OUTGOING current
  // ride's own status this render, not the incoming one:
  //   - it left IN_PROGRESS (confirmed / completed / final / scratched) ->
  //     CURRENT moved because the ride the operator was looking at is DONE,
  //     not because the ring genuinely moved on. Don't scroll — #919.
  //   - it's still IN_PROGRESS -> a later ride has genuinely overtaken it
  //     (#921's multi-IN_PROGRESS case, e.g. the next horse already entered
  //     before the previous one's marks were confirmed) -> that's real ring
  //     progression, follow it.
  //   - no previous current at all -> first paint, scroll straight to it.
  // The "back to current" FAB is always there for the operator to jump
  // manually regardless of which case suppressed the auto-scroll.
  //
  // statusByRideId is kept in a REF, not a scroll-effect dependency: `rows`
  // (and therefore a status map derived from it) gets a new reference on
  // EVERY poll tick, including ones where currentRideId itself doesn't
  // change. Depending on it directly would re-run the decision below — and
  // therefore potentially re-scroll — on every tick, not only on an actual
  // CURRENT change. The effect must stay keyed on currentRideId alone,
  // exactly like the pre-#919 version, and just read the freshest status
  // snapshot when it does fire.
  const statusByRideIdRef = useRef(new Map());
  useEffect(() => {
    const map = new Map();
    for (const row of rows) {
      if (row.kind === ROW_KIND.RIDE) map.set(row.id, row.status);
    }
    statusByRideIdRef.current = map;
  }, [rows]);

  // #2131 — currentRideId (below) now comes from the ring's stored
  // ring-state row, polled on `entityTick`; row.status (statusByRideIdRef,
  // above) comes from the SEPARATE useRideStatuses poll, on its own
  // STATUS_POLL_MS timer. Those two timers are independent, so the render
  // where currentRideId first reflects a reassignment can predate the render
  // where the outgoing ride's OWN status has caught up — deciding
  // outgoingStillInProgress off whatever statusByRideIdRef happens to hold AT
  // THAT INSTANT reads stale data and wrongly auto-scrolls on a plain confirm
  // (#919's bug, reintroduced by the poll race). Force a fresh status fetch
  // the moment a reassignment is seen, and DEFER the decision to the second
  // effect below, which fires once that fresh snapshot actually lands.
  const pendingReassignmentRef = useRef(null);
  const prevCurrentRideIdRef = useRef(null);
  useEffect(() => {
    if (!currentRideId) return;
    const prevCurrentRideId = prevCurrentRideIdRef.current;
    const isReassignment = prevCurrentRideId && prevCurrentRideId !== currentRideId;
    if (!isReassignment) {
      scrollToCurrent();
    } else {
      pendingReassignmentRef.current = prevCurrentRideId;
      refreshStatuses();
    }
    prevCurrentRideIdRef.current = currentRideId;
  }, [currentRideId, scrollToCurrent, refreshStatuses]);

  // Resolves a reassignment queued above once a status snapshot taken AFTER
  // the refreshStatuses() call lands. `rows` (and therefore
  // statusByRideIdRef) updates first on that same render — its effect is
  // declared earlier, above — so this always reads the fresh value.
  useEffect(() => {
    const prevCurrentRideId = pendingReassignmentRef.current;
    if (!prevCurrentRideId) return;
    pendingReassignmentRef.current = null;
    const outgoingStillInProgress =
      statusByRideIdRef.current.get(prevCurrentRideId) === RIDE_STATUSES.IN_PROGRESS;
    if (outgoingStillInProgress) scrollToCurrent();
  }, [statuses, scrollToCurrent]);

  useEffect(() => {
    if (!currentRideId) {
      setCurrentOffScreen(false);
      return undefined;
    }
    const check = () => {
      const el = currentRef.current;
      if (!el) return;
      const { top, bottom } = el.getBoundingClientRect();
      setCurrentOffScreen(bottom < 0 || top > window.innerHeight);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [currentRideId, rows.length]);

  const loading = (classesLoading || ridesLoading) && rows.length === 0;

  return (
    // #1645 — overflowAnchor: "none" opts the WHOLE page out of the browser's
    // native scroll anchoring. Rows above the viewport constantly change
    // height here (a RideRow Accordion expanding/collapsing, a ClassResults
    // box appearing when a class reveals, the 5s poll re-rendering the list) —
    // the browser's own anchor heuristic was compensating for that by moving
    // the window's scroll position on its own, with NO scrollIntoView call
    // involved anywhere. That happened regardless of autoScrollEnabled, which
    // is why turning the toggle off did not actually stop the view from
    // moving — scrollToCurrent (below) was never the only thing moving it.
    <Box
      sx={{ minHeight: "100vh", bgcolor: "background.default", pb: 12, overflowAnchor: "none" }}
    >
      {/* #691 — sticky header: everything that was previously "above the
          Classes list" (show/ring, freshness banner), plus
          the live venue clock, the next-event countdown, and the Auto Scroll
          switch. Stays pinned while the rest of the page scrolls under it.
          #2112 — `position: "fixed"` (not `sticky`, see headerRef's comment
          above): pins below the app shell's AppBar (top: 64px, that bar's own
          fixed height, App.jsx/AppLayout.jsx) regardless of any ancestor's
          `overflow`, and sits under the AppBar's zIndex: 1300. The spacer Box
          right after it (headerHeight, ResizeObserver-measured) reserves the
          space this header would otherwise occupy in normal flow. */}
      <Box
        ref={headerRef}
        data-testid="oog-sticky-header"
        sx={{
          position: "fixed",
          top: 64,
          left: 0,
          right: 0,
          zIndex: 1250,
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          px: 3,
          pt: 3,
          pb: 2,
        }}
      >
        <Box sx={{ maxWidth: 1100, mx: "auto" }}>
          {/* #1875 — sizing rule, not a one-off tweak for the reported name:
              the row is allowed to WRAP AS A WHOLE (flexWrap) so a narrow
              viewport drops the clock block onto its own row instead of
              squeezing the title/clock Typography below their content width
              (the old failure mode — flex items shrink before text wraps,
              and neither Typography had whiteSpace: nowrap, so long content
              spilled onto a second line INSIDE the heading itself). */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
            flexWrap="wrap"
            rowGap={1}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 320px" }}>
              {/* Show names are operator-entered free text with no length
                  cap (real names run 80-150+ chars, e.g. multi-week WEC/USDF
                  regional championship titles) — no fixed font size can
                  guarantee a fit, so this truncates with an ellipsis instead
                  of wrapping. `minWidth: 0` above is required for the
                  ellipsis to ever engage inside a flex item — a flex item's
                  default min-width is its content's natural (unwrapped)
                  width, which would keep the box exactly as wide as the
                  title and defeat both the truncation and the flexWrap
                  fallback above. */}
              <Typography
                variant="h5"
                fontWeight={800}
                data-testid="oog-show-name"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedShow?.name}
              </Typography>
              {/* #1646, arena tabs: one MUI Tab per ring on THIS show, in the
                  deterministic order `ringsFor` now sorts (natural-numeric,
                  identical across polls/reloads/cache refetches, so an arena
                  never moves out from under the announcer). Selecting a tab
                  calls the ungated AnnouncerContext.switchRing immediately
                  (no PIN, replaces #926's Chip pill row). #2112 — org/show
                  change is the app shell's OrgSwitcher/ShowSwitcher (App.jsx),
                  free like ring always was, no lock of any kind involved.
                  Lives inside the same sticky header Box as before, so the
                  tabs stay pinned while the Order of Go list scrolls beneath
                  them. Selected tab keeps the pill row's proven brand
                  foxOrange + black-text treatment; unselected tabs are
                  outlined with `text.primary`/`divider`, legible in both
                  themes, avoiding brand `primary.main`'s near-black grey on
                  dark (#862). Default MUI indicator bar is hidden since the
                  filled background already signals the active tab. */}
              <Tabs
                value={rings.includes(selectedRing) ? selectedRing : false}
                onChange={(_e, newRing) => handleRingSwitch(newRing)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                data-testid="oog-ring"
                data-ring-resolved={ringResolved}
                TabIndicatorProps={{ sx: { display: "none" } }}
                sx={{
                  mt: 1,
                  minHeight: 0,
                  "& .MuiTabs-flexContainer": { gap: 1 },
                }}
              >
                {rings.map((ring) => {
                  const active = ring === selectedRing;
                  return (
                    <Tab
                      key={ring}
                      value={ring}
                      data-testid={`ring-tab-${ring}`}
                      label={`${t("setup.ringLabel")} ${ring}`}
                      sx={
                        active
                          ? {
                              minHeight: 0,
                              py: 0.75,
                              px: 2,
                              borderRadius: 1,
                              border: 1,
                              textTransform: "none",
                              fontSize: "1rem",
                              fontWeight: 800,
                              bgcolor: colors.foxOrange,
                              color: "#000",
                              borderColor: colors.foxOrange,
                              "&:hover": { bgcolor: colors.foxOrange },
                              "&.Mui-selected": { color: "#000" },
                            }
                          : {
                              minHeight: 0,
                              py: 0.75,
                              px: 2,
                              borderRadius: 1,
                              border: 1,
                              textTransform: "none",
                              fontSize: "1rem",
                              fontWeight: 700,
                              color: "text.primary",
                              borderColor: "divider",
                            }
                      }
                    />
                  );
                })}
              </Tabs>
            </Box>

            <Stack direction="row" spacing={3} alignItems="flex-start" flexShrink={0}>
              <Box sx={{ textAlign: "right" }}>
                {/* #1875 — down from h3, plus whiteSpace: nowrap. The clock's
                    own text ("5:46:07 PM") is bounded (formatVenueClock
                    always emits the same shape), so nowrap alone is enough
                    to guarantee one line here — unlike the show name above,
                    it never needs truncation. The outer Stack's flexWrap is
                    what protects it on a narrow viewport: this whole block
                    drops to its own row rather than the clock text
                    squeezing and wrapping in place. */}
                <Typography
                  variant="h4"
                  fontWeight={800}
                  data-testid="oog-clock"
                  sx={{
                    color: "text.primary",
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  {/* #1813 — the header clock is a genuine instant (unlike
                      the FVD-stamp ride/class times below it), so it renders
                      through the show's real venueTimezone, not the fixed FVD
                      stamp zone formatTime uses. Falls back to Pacific inside
                      formatVenueClock when the show has none set. */}
                  {formatVenueClock(liveNow, selectedShow?.venueTimezone)}
                </Typography>
                <Typography
                  variant="body2"
                  data-testid="oog-countdown"
                  sx={{ color: "text.secondary" }}
                >
                  {nextEvent
                    ? t("oog.nextEvent", {
                        label: nextEventLabel(nextEvent, t),
                        countdown: formatCountdown(
                          new Date(nextEvent.scheduledTime).getTime() - liveNow,
                        ),
                      })
                    : t("oog.nextEventNone")}
                </Typography>
              </Box>

              {/* #908 — manual refresh, in the sticky header's own top row
                  (not a viewport-fixed overlay — that positioned relative to
                  the CSS layout viewport and, on a narrower/actual window,
                  landed on top of the Sign Out button and clipped it; a
                  normal flex item here can never overlap by construction).
                  Own bg/border/shadow, NOT color="primary" — brand
                  primary.main is a near-black grey that all but disappears
                  in dark mode (#862) — matching scribe-fe's #888 refresh
                  button. */}
              <Tooltip title={refreshing ? t("oog.refreshing") : t("oog.refresh")}>
                <span>
                  <IconButton
                    data-testid="oog-refresh-button"
                    aria-label={t("oog.refresh")}
                    onClick={handleRefresh}
                    disabled={refreshing}
                    sx={{
                      bgcolor: "background.paper",
                      color: "text.primary",
                      border: 1,
                      borderColor: "divider",
                      boxShadow: 2,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <RefreshIcon
                      fontSize="small"
                      sx={
                        refreshing
                          ? {
                              animation: "oogRefreshSpin 1s linear infinite",
                              "@keyframes oogRefreshSpin": {
                                from: { transform: "rotate(0deg)" },
                                to: { transform: "rotate(360deg)" },
                              },
                            }
                          : undefined
                      }
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={3} flexWrap="wrap">
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={autoScrollEnabled}
                  onChange={(e) => setAutoScrollEnabled(e.target.checked)}
                  // #1857 — disabled until `selectedRing` resolves off `null`
                  // (AnnouncerContext's own class-list fetch, #1740): a click
                  // before then has no valid ring key to persist under
                  // (updateRingSetting's own guard, above) and would leave the
                  // switch reading DEFAULT_RING_SETTINGS regardless of what
                  // was clicked. Disabling means Cypress's own actionability
                  // retry on `.click()` naturally waits for resolution rather
                  // than firing into that dead window.
                  disabled={!selectedRing}
                  data-testid="auto-scroll-toggle"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: colors.foxOrange },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: colors.foxOrange,
                    },
                  }}
                />
              }
              label={t("oog.autoScrollLabel")}
              slotProps={{ typography: { sx: { color: "text.primary" } } }}
            />

            {/* #892 — all-rides vs today-only. Coexists with Auto Scroll in
                the same row rather than competing for the same switch or
                fighting #909's scroll gating: this toggle only ever changes
                WHICH rides feed buildOrderOfGoRows, never scroll behavior. */}
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={showAllDays}
                  onChange={(e) => setShowAllDays(e.target.checked)}
                  // #1857 — same unresolved-ring guard as auto-scroll-toggle.
                  disabled={!selectedRing}
                  data-testid="show-all-days-toggle"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: colors.foxOrange },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: colors.foxOrange,
                    },
                  }}
                />
              }
              label={t("oog.showAllDaysLabel")}
              slotProps={{ typography: { sx: { color: "text.primary" } } }}
            />
          </Stack>

          {/* #645 — persistent, honest freshness banner. Shows whenever the app
              is offline OR the data on screen has gone stale despite being
              nominally online; clears itself the moment a refresh lands. */}
          {showFreshnessBanner ? (
            <Alert severity="warning" sx={{ mt: 2 }} data-testid="oog-not-live">
              {lastUpdated
                ? t("oog.notLive", { time: formatClockTime(lastUpdated) })
                : t("oog.notLiveNoData")}
            </Alert>
          ) : null}
        </Box>
      </Box>
      {/* #2112 — reserves the space the now position:fixed header above
          would otherwise occupy in normal flow (see headerRef's comment). */}
      <Box sx={{ height: headerHeight }} data-testid="oog-header-spacer" />

      <Box sx={{ px: 3, maxWidth: 1100, mx: "auto" }}>
        {/* #714 — the running-order list not yet loaded: shaped skeleton
            rows instead of a bare spinner, matching the table-data
            convention used everywhere else. */}
        {loading ? (
          <Box sx={{ mt: 3 }} data-testid="oog-loading">
            <TableSkeleton rows={8} columns={4} />
          </Box>
        ) : null}

        {!loading && rows.length === 0 ? (
          <Typography sx={{ mt: 8 }} color="text.secondary" data-testid="oog-empty">
            {t("oog.empty")}
          </Typography>
        ) : null}

        {/* #2197 — rows are known (rows.length > 0) but ring-state hasn't
            answered for the CURRENT ring yet (!ringStateKnown, see that
            var's own comment above). Say nothing final either way rather
            than guess. A compact, uniquely-testid'd indicator — deliberately
            never TableSkeleton, a shared testid across two render branches
            is exactly the vacuous-assertion trap a sibling card was denied
            for — so a slow ring-state fetch reads as "still checking," not
            a silent gap and not the empty-ring answer. */}
        {!loading && rows.length > 0 && !ringStateKnown ? (
          <Skeleton
            variant="rounded"
            height={48}
            sx={{ mt: 2 }}
            data-testid="oog-ring-state-loading"
          />
        ) : null}

        {/* #2131 (docs/score/ring-truth.md rule 8) — with no valid ring
            state, announcer-fe says PLAINLY that no class is in progress. It
            never invents a class (no row anywhere gets RIDE_ROLE.CURRENT,
            assignRoles above) and never shows an error. #2197 corrects a
            stale claim here: useRingStateForRing's OWN first-ever settle
            happens on mount, before any real ring is even known (the
            `!showId || !ring` branch, now `undefined` rather than `null`) —
            so by the time the real, ring-resolved fetch ever runs,
            useRemoteResource's `hasLoadedRef` is already true, EXCEPT right
            after a ring/show switch (#2217's `resourceKey`), which resets
            `hasLoadedRef` back to false so a real fetch can cold-start-fail
            again. #2227 corrects a second stale claim #2197 made here: that
            reset used to make the `{ data: null, error }` COLD-START branch
            genuinely unreachable ("full stop") was only ever true before
            #2217 added the reset; #2217 re-armed it, and #2227 is the fix,
            not just a comment update: useRingStateForRing now passes
            `undefined` as `initialValue` (docs in useRemoteResource.js and
            useRingStateForRing.js), so BOTH the reset and a cold-start
            failure on the new ring settle `data` to `undefined`, the same
            "not answered yet" sentinel as the pre-ring-resolved case, not
            `null`. A failure therefore always leaves `data` at either
            `undefined` (ringStateKnown false, renders the Skeleton above,
            covers a fresh mount, a not-yet-resolved ring, AND a failed
            first fetch after a reset) or the previous real answer (CURRENT
            role and rows untouched, a same-key revalidate failure). Gated
            on rows.length > 0 so it never doubles up with oog.empty above
            (that's "nothing scheduled at all", a different fact from
            "nothing is live right now"), and on ringStateKnown (#2197, not
            the raw `ringStateLoading` flag) so this can no longer render
            before ring-state has actually answered for the selected ring:
            `ringStateLoading` alone settles once near mount and stays false
            through the later, ring-resolved fetch's own in-flight window,
            which used to let this banner race ahead of the real answer. */}
        {!loading && ringStateKnown && rows.length > 0 && !ringTruth.inProgress ? (
          <Alert severity="info" sx={{ mt: 2 }} data-testid="oog-no-class-in-progress">
            {t("oog.noClassInProgress")}
          </Alert>
        ) : null}

        <Stack spacing={1.5} sx={{ mt: 2 }} data-testid="oog-list">
          {displayRows.map((row) => {
            if (row.kind === ROW_KIND.DAY_HEADER) {
              return <DayHeader key={row.id} row={row} />;
            }
            if (row.kind === ROW_KIND.CLASS_HEADER) {
              return (
                <Box key={row.id}>
                  <ClassHeader row={row} />
                  <ClassResults
                    classId={row.classId}
                    isLive={row.classId === liveClassId}
                    rideRows={rideRowsByClassId.get(row.classId) || []}
                    onResults={handleClassResults}
                    online={browserOnline}
                    statusesUnknown={statusesUnknown}
                  />
                </Box>
              );
            }
            if (row.kind === ROW_KIND.BREAK) {
              return <BreakRow key={row.id} row={row} />;
            }
            const classResults = resultsByClassId[row.classId];
            const rideResult = classResults?.find((r) => r.rideId === row.id) || null;
            return (
              <RideRow
                key={row.id}
                row={row}
                result={rideResult}
                ref={row.role === RIDE_ROLE.CURRENT ? currentRef : null}
              />
            );
          })}
        </Stack>
      </Box>

      {currentOffScreen && autoScrollEnabled ? (
        <Fab
          variant="extended"
          data-testid="back-to-current"
          onClick={scrollToCurrent}
          sx={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            bgcolor: colors.foxOrange,
            color: "#000",
            fontWeight: 800,
            "&:hover": { bgcolor: colors.foxOrange },
          }}
        >
          <ArrowDownwardIcon sx={{ mr: 1 }} />
          {t("oog.backToCurrent")}
        </Fab>
      ) : null}
    </Box>
  );
}
